/**
 * Sending a crew call (producer / shoot lead): shared by the comms drawer's call pad and the command
 * view's Calls tab, so both show the same busy state and the same error.
 */
import { useCallback } from 'react';
import { create } from 'zustand';
import type { BroadcastPreset } from '@apos/shared';
import { toast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useMission } from '../../state/mission';

/** Server limit for a custom broadcast. */
export const BROADCAST_MAX = 120;

const useCallFlights = create<{ ids: Set<string> }>(() => ({ ids: new Set() }));

export function useSendCall(): { send(preset: BroadcastPreset, custom?: string): Promise<boolean>; busy: boolean } {
  const missionId = useMission((s) => s.bundle?.mission.id ?? null);
  const busy = useCallFlights(s => missionId != null && s.ids.has(missionId));
  const send = useCallback(
    async (preset: BroadcastPreset, customText?: string) => {
      const custom = preset === 'CUSTOM' ? (customText ?? '').trim().slice(0, BROADCAST_MAX) : undefined;
      if (!missionId || (preset === 'CUSTOM' && !custom) || useCallFlights.getState().ids.has(missionId)) return false;
      useCallFlights.setState(s => ({ ids: new Set([...s.ids, missionId]) }));
      try {
        const bc = await api.broadcasts.send(missionId, preset, custom);
        if (useMission.getState().bundle?.mission.id === missionId) useMission.getState().ingest([{ type: 'broadcast', data: bc }]);
        return true;
      } catch (e) {
        if (useMission.getState().bundle?.mission.id === missionId) toast({ title: 'Broadcast not sent', body: errorMessage(e), tone: 'alert' });
        return false;
      } finally {
        useCallFlights.setState(s => ({ ids: new Set([...s.ids].filter(id => id !== missionId)) }));
      }
    },
    [missionId],
  );
  return { send, busy };
}
